import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Selector de motivo de ausencia justificada (catálogo empresa + «Otros»).
 * Emite el texto final en `onChange(motivoTexto)`.
 */
export default function MotivoAusenciaJustificadaFields({
  value = "",
  onChange,
  label = "Motivo",
  testId = "ausencia-motivo-input",
  requiredHint = true,
}) {
  const [motivos, setMotivos] = useState([]);
  const [sel, setSel] = useState("");
  const [otro, setOtro] = useState("");

  useEffect(() => {
    api
      .get("/motivos-incidencia", { params: { solo_activos: true, ambito: "ausencia_justificada" } })
      .then(({ data }) => setMotivos(data || []))
      .catch(() => setMotivos([]));
  }, []);

  useEffect(() => {
    if (!motivos.length) return;
    // Si ya hay valor externo (edición), intentar mapear
    if (value && !sel) {
      const match = motivos.find((m) => m.nombre === value);
      if (match) {
        setSel(String(match.id));
        return;
      }
      const otros = motivos.find((m) => m.es_otros);
      if (otros) {
        setSel(String(otros.id));
        setOtro(value);
      }
    }
  }, [motivos]); // eslint-disable-line react-hooks/exhaustive-deps

  const emitir = (idStr, textoOtro) => {
    const m = motivos.find((x) => String(x.id) === String(idStr));
    if (!m) {
      onChange?.(textoOtro || "");
      return;
    }
    if (m.es_otros) {
      onChange?.(textoOtro.trim());
    } else {
      onChange?.(m.nombre);
    }
  };

  const seleccionado = motivos.find((m) => String(m.id) === String(sel));
  const esOtros = !!seleccionado?.es_otros;

  if (!motivos.length) {
    return (
      <div className="space-y-1.5">
        <Label>{label}</Label>
        <Textarea
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="Describe el motivo"
          data-testid={testId}
        />
        {requiredHint && <p className="text-[11px] text-zinc-400">Mínimo 3 caracteres</p>}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label>{label}</Label>
        <Select
          value={sel || undefined}
          onValueChange={(v) => {
            setSel(v);
            const m = motivos.find((x) => String(x.id) === v);
            if (m?.es_otros) {
              emitir(v, otro);
            } else {
              setOtro("");
              emitir(v, "");
            }
          }}
        >
          <SelectTrigger data-testid={`${testId}-select`}>
            <SelectValue placeholder="Selecciona un motivo" />
          </SelectTrigger>
          <SelectContent>
            {motivos.map((m) => (
              <SelectItem key={m.id} value={String(m.id)}>
                {m.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {esOtros && (
        <div className="space-y-1.5">
          <Label>Especifica el motivo</Label>
          <Textarea
            value={otro}
            onChange={(e) => {
              setOtro(e.target.value);
              emitir(sel, e.target.value);
            }}
            placeholder="Describe el motivo"
            data-testid={testId}
          />
        </div>
      )}
      {requiredHint && (
        <p className="text-[11px] text-zinc-400">
          Según convenio pueden ampliarse estos supuestos.{" "}
          {esOtros ? "Indica al menos 3 caracteres." : "El motivo del catálogo se enviará automáticamente."}
        </p>
      )}
    </div>
  );
}
